export type ClaimSubmitDetailError = {
  code: string;
  message: string;
};

/** User-facing copy for claim-intake API / Dify failures (do not surface raw workflow errors). */
export const CLAIM_INTAKE_GENERIC_ERROR_MESSAGE =
  'Something went wrong, please try after some time.';

/** PCMI 5xx from workflow_finished — hide technical payload in the error modal. */
export const PCMI_SERVER_ERROR_MESSAGE =
  'Something went wrong, please try after sometime';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function firstNonEmpty(a: string, b: string): string {
  const t1 = typeof a === 'string' ? a.trim() : '';
  if (t1) return t1;
  const t2 = typeof b === 'string' ? b.trim() : '';
  return t2;
}

/** True when a string is (or starts like) a raw API JSON payload, not a user message. */
export function isRawJsonText(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false;
  if (
    trimmed.includes('"status_code"') ||
    trimmed.includes('"statusCode"') ||
    trimmed.includes('"correlationId"')
  ) {
    return true;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return trimmed.includes('"message"') || trimmed.includes('"data"');
  }
}

function isGenericApiWrapperMessage(value: string): boolean {
  switch (value.trim().toLowerCase()) {
    case 'client api error':
    case 'bad request':
    case 'internal server error':
    case 'unauthorized':
    case 'forbidden':
    case 'request failed':
    case 'error':
      return true;
    default:
      return false;
  }
}

function asUserFacingMessage(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || isRawJsonText(trimmed)) return '';
  return trimmed;
}

function toUserFacingSummary(summary: string, details: ClaimSubmitDetailError[]): string {
  const cleaned = asUserFacingMessage(summary);
  if (!cleaned || isGenericApiWrapperMessage(cleaned)) {
    return details.length > 0 ? '' : CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
  }
  const matchesDetail = details.some((d) => d.message.trim() === cleaned);
  if (details.length > 0 && matchesDetail) return '';
  return cleaned;
}

function sanitizeClaimSubmitErrorResult(result: ClaimSubmitErrorParseResult): ClaimSubmitErrorParseResult {
  const details = result.details.filter((d) => asUserFacingMessage(d.message));
  return {
    ...result,
    summary: toUserFacingSummary(result.summary, details),
    details,
  };
}

/** First user-readable line for toasts — never the raw JSON envelope. */
export function getClaimSubmitUserMessage(content: ClaimSubmitErrorModalContent): string {
  const summary = asUserFacingMessage(content.summary ?? '');
  if (summary && !isGenericApiWrapperMessage(summary)) return summary;
  const fromDetail = content.details.find((d) => asUserFacingMessage(d.message))?.message.trim();
  return fromDetail || CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
}

function coerceJsonValue(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  if (!s.startsWith('{') && !s.startsWith('[')) return v;
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return v;
  }
}

function collectFromErrorBlock(
  errorBlock: unknown,
  out: ClaimSubmitDetailError[],
  seen: Set<string>
): string {
  if (!isRecord(errorBlock)) return '';
  const summary = typeof errorBlock.message === 'string' ? errorBlock.message : '';
  appendDetailRowsFromDetailsArray(errorBlock.details, out, seen);
  return summary;
}

function mergeMessageField(
  message: unknown,
  summary: string,
  out: ClaimSubmitDetailError[],
  seen: Set<string>
): string {
  const coerced = coerceJsonValue(message);
  if (typeof coerced === 'string' && coerced.trim()) {
    if (coerced.trim().startsWith('{') || coerced.trim().startsWith('[')) {
      return mergeMessageField(coerceJsonValue(coerced), summary, out, seen);
    }
    return firstNonEmpty(summary, coerced);
  }
  if (!isRecord(coerced)) return summary;
  const nested = collectFromErrorBlock(coerced.error, out, seen);
  return firstNonEmpty(summary, nested);
}

function mergeDataArray(
  data: unknown,
  summary: string,
  out: ClaimSubmitDetailError[],
  seen: Set<string>
): string {
  const coerced = coerceJsonValue(data);
  if (!Array.isArray(coerced)) return summary;
  let next = summary;
  for (const entry of coerced) {
    if (!isRecord(entry)) continue;
    next = mergeMessageField(entry.message, next, out, seen);
  }
  return next;
}

function normalizeClaimSubmitPayload(err: unknown): unknown {
  let node = coerceJsonValue(err);
  if (typeof node === 'string') node = coerceJsonValue(node);

  if (!isRecord(node)) return node;

  const outputs = node.outputs;
  if (isRecord(outputs) && outputs.text != null) {
    return normalizeClaimSubmitPayload(outputs.text);
  }

  const nestedData = node.data;
  if (isRecord(nestedData)) {
    const nestedOutputs = nestedData.outputs;
    if (isRecord(nestedOutputs) && nestedOutputs.text != null) {
      return normalizeClaimSubmitPayload(nestedOutputs.text);
    }
    const innerWorkflowData = nestedData.data;
    if (isRecord(innerWorkflowData)) {
      const innerOutputs = innerWorkflowData.outputs;
      if (isRecord(innerOutputs) && innerOutputs.text != null) {
        return normalizeClaimSubmitPayload(innerOutputs.text);
      }
    }
  }

  return node;
}

function unwrapMutationPayload(err: unknown): unknown {
  const normalized = normalizeClaimSubmitPayload(err);
  if (!isRecord(normalized)) return coerceJsonValue(normalized);

  if (readHttpStatusCode(normalized) != null) return normalized;

  const errNested = normalized.error;
  const fromErrorData = isRecord(errNested) && 'data' in errNested ? errNested.data : undefined;
  const fromData = 'data' in normalized ? normalized.data : undefined;
  const raw = fromErrorData ?? fromData ?? normalized;
  return coerceJsonValue(raw);
}

function detailRowDedupeKey(code: string, message: string): string {
  return `${code}\0${message}`;
}

function appendDetailRowsFromDetailsArray(
  detailsArr: unknown,
  out: ClaimSubmitDetailError[],
  seen: Set<string>
): void {
  if (!Array.isArray(detailsArr)) return;
  for (const row of detailsArr) {
    if (!isRecord(row)) continue;
    const msg = row.message;
    if (typeof msg === 'string' && msg.trim()) {
      const code = typeof row.code === 'string' ? row.code : '';
      const key = detailRowDedupeKey(code, msg);
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ code, message: msg });
      }
    }
    if (row.details != null) {
      appendDetailRowsFromDetailsArray(row.details, out, seen);
    }
  }
}

function scrapeDetailsDeep(node: unknown, out: ClaimSubmitDetailError[], seen: Set<string>): void {
  if (node == null) return;
  if (typeof node === 'string') {
    const parsed = coerceJsonValue(node);
    if (parsed === node) return;
    scrapeDetailsDeep(parsed, out, seen);
    return;
  }
  if (Array.isArray(node)) {
    for (const x of node) scrapeDetailsDeep(x, out, seen);
    return;
  }
  if (!isRecord(node)) return;

  appendDetailRowsFromDetailsArray(node.details, out, seen);
  for (const v of Object.values(node)) scrapeDetailsDeep(v, out, seen);
}

function readHttpStatusCode(node: unknown): number | null {
  if (!isRecord(node)) return null;
  const raw = node.status_code ?? node.statusCode;
  const num = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
  if (typeof num !== 'number' || !Number.isFinite(num)) return null;
  return num;
}

function coerceStatusNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number.parseInt(raw.trim(), 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** PCMI HTTP status from workflow_finished outputs / parsed `outputs.text`. */
export function readPcmiStatusCode(data: unknown): number | null {
  const visit = (node: unknown, depth: number): number | null => {
    if (depth > 6 || node == null) return null;
    const coerced = coerceJsonValue(node);
    if (typeof coerced === 'string') {
      return visit(coerceJsonValue(coerced), depth + 1);
    }
    if (Array.isArray(coerced)) {
      for (const item of coerced) {
        const found = visit(item, depth + 1);
        if (found != null) return found;
      }
      return null;
    }
    if (!isRecord(coerced)) return null;
    const direct = coerceStatusNumber(coerced.pcmi_status_code );
    if (direct != null) return direct;
    const outputs = coerced.outputs;
    if (isRecord(outputs)) {
      const fromOutputs = coerceStatusNumber(outputs.pcmi_status_code );
      if (fromOutputs != null) return fromOutputs;
      const fromText = visit(outputs.text, depth + 1);
      if (fromText != null) return fromText;
    }
    if ('text' in coerced) {
      const fromText = visit(coerced.text, depth + 1);
      if (fromText != null) return fromText;
    }
    if ('data' in coerced) {
      const fromData = visit(coerced.data, depth + 1);
      if (fromData != null) return fromData;
    }
    return null;
  };
  return visit(data, 0);
}

export function isPcmiServerErrorStatus(code: number | null): boolean {
  return code != null && code > 500;
}

function readWorkflowOutputErrorMessage(data: unknown): string {
  const root = normalizeClaimSubmitPayload(data);

  if (typeof root === 'string') {
    const trimmed = root.trim();
    if (!trimmed) return '';
    if (isGeneratedClaimNumberString(trimmed)) return '';
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = coerceJsonValue(trimmed);
      if (parsed !== root) return readWorkflowOutputErrorMessage(parsed);
      return '';
    }
    return trimmed;
  }

  if (!isRecord(root)) return '';

  const directError = root.error;
  if (typeof directError === 'string' && directError.trim()) {
    const trimmed = directError.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = coerceJsonValue(trimmed);
      if (parsed !== directError) return readWorkflowOutputErrorMessage(parsed);
      return '';
    }
    return trimmed;
  }

  const textVal = root.text;
  if (typeof textVal === 'string') {
    const fromText = readWorkflowOutputErrorMessage(coerceJsonValue(textVal) ?? textVal);
    if (fromText) return fromText;
  }
  if (isRecord(textVal)) {
    const nestedErr = textVal.error;
    if (typeof nestedErr === 'string' && nestedErr.trim()) {
      return readWorkflowOutputErrorMessage(nestedErr);
    }
  }

  return '';
}

function appendWorkflowOutputErrorDetails(
  workflowMsg: string,
  summary: string,
  details: ClaimSubmitDetailError[],
  seen: Set<string>
): string {
  const userMsg = asUserFacingMessage(workflowMsg);
  if (!userMsg) return summary;
  const next = firstNonEmpty(summary, userMsg);
  const key = detailRowDedupeKey('', userMsg);
  if (!seen.has(key)) {
    seen.add(key);
    details.push({ code: '', message: userMsg });
  }
  return next;
}

export function isClaimSubmitWorkflowErrorResponse(data: unknown): boolean {
  if (isPcmiServerErrorStatus(readPcmiStatusCode(data))) return true;
  if (readWorkflowOutputErrorMessage(data)) return true;

  const normalized = normalizeClaimSubmitPayload(data);
  const top = readHttpStatusCode(normalized);
  if (top != null && top >= 400) return true;

  if (isRecord(normalized) && Array.isArray(normalized.data)) {
    for (const entry of normalized.data) {
      const entryStatus = readHttpStatusCode(entry);
      if (entryStatus != null && entryStatus >= 400) return true;
    }
  }

  const { details } = parseClaimSubmitMutationError(data);
  return details.length > 0;
}

export type ClaimSubmitErrorParseResult = {
  summary: string;
  details: ClaimSubmitDetailError[];
  claimNumber: string;
};

export type ClaimSubmitErrorModalContent = {
  summary: string;
  details: ClaimSubmitDetailError[];
  claimNumber?: string;
  heading?: string;
};

export function buildGenericClaimIntakeErrorModal(
  options?: Readonly<{ heading?: string; claimNumber?: string }>
): ClaimSubmitErrorModalContent {
  return {
    summary: CLAIM_INTAKE_GENERIC_ERROR_MESSAGE,
    details: [],
    ...(options?.heading ? { heading: options.heading } : {}),
    ...(options?.claimNumber ? { claimNumber: options.claimNumber } : {}),
  };
}

export type ClaimIntakeErrorPresentation =
  | { kind: 'modal'; modal: ClaimSubmitErrorModalContent }
  | { kind: 'inline'; message: string };

function unwrapClaimIntakeThrownError(err: unknown): unknown {
  if (err == null) return err;
  if (err instanceof Error) return err;
  if (!isRecord(err)) return err;

  if ('data' in err && err.data != null) return err.data;

  const nestedError = err.error;
  if (isRecord(nestedError)) {
    if ('data' in nestedError && nestedError.data != null) return nestedError.data;
    return nestedError;
  }

  return err;
}

/**
 * Chooses modal vs inline messaging for claim-intake API failures.
 * By default surfaces a generic user message. Pass `preserveDetails: true` for VIN lookup /
 * submit flows that should show structured API detail rows (e.g. "Contract not found.").
 */
export function presentClaimIntakeError(
  err: unknown,
  options?: Readonly<{ heading?: string; fallback?: string; preserveDetails?: boolean }>
): ClaimIntakeErrorPresentation {
  const unwrapped = unwrapClaimIntakeThrownError(err);

  if (options?.preserveDetails) {
    const vinDetails = extractVinLookupDetailErrors(unwrapped);
    const parsed = parseClaimSubmitMutationError(unwrapped);
    const details = vinDetails.length > 0 ? vinDetails : parsed.details;
    if (details.length > 0) {
      const summary =
        details.find((d) => d.message.trim())?.message.trim() ||
        parsed.summary.trim() ||
        options.fallback ||
        CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
      return {
        kind: 'modal',
        modal: {
          ...(options.heading ? { heading: options.heading } : {}),
          summary,
          details,
          ...(parsed.claimNumber ? { claimNumber: parsed.claimNumber } : {}),
        },
      };
    }
  }

  return { kind: 'inline', message: CLAIM_INTAKE_GENERIC_ERROR_MESSAGE };
}

export function parseClaimSubmitWorkflowError(data: unknown): ClaimSubmitErrorParseResult {
  if (isPcmiServerErrorStatus(readPcmiStatusCode(data))) {
    return {
      summary: PCMI_SERVER_ERROR_MESSAGE,
      details: [],
      claimNumber: readCreateClaimClaimNumber(data),
    };
  }
  const parsed = parseClaimSubmitMutationError(data);
  return {
    summary: parsed.summary,
    details: parsed.details,
    claimNumber: parsed.claimNumber,
  };
}

/**
 * Client/VIN API errors nest actionable messages at `data[].message.error.details[]`.
 * Prefer those detail rows over top-level summaries such as "Client API Error".
 */
function extractVinLookupDetailErrors(err: unknown): ClaimSubmitDetailError[] {
  const root = normalizeClaimSubmitPayload(err);
  if (!isRecord(root)) return [];

  const data = root.data;
  if (!Array.isArray(data)) return [];

  const out: ClaimSubmitDetailError[] = [];
  const seen = new Set<string>();

  for (const entry of data) {
    if (!isRecord(entry)) continue;
    const message = entry.message;
    if (!isRecord(message)) continue;
    const error = message.error;
    if (!isRecord(error)) continue;
    appendDetailRowsFromDetailsArray(error.details, out, seen);
  }

  return out;
}

/**
 * Surfaces VIN/contract lookup detail messages (e.g. "Contract not found.").
 * Other failures fall back to the generic claim-intake message.
 */
export function resolveVinLookupErrorMessage(err: unknown): string {
  const targetedDetails = extractVinLookupDetailErrors(err);
  const targetedMessage = targetedDetails.find((detail) => detail.message.trim())?.message.trim();
  if (targetedMessage) return targetedMessage;

  const parsed = parseClaimSubmitMutationError(err);
  const fallbackDetail = parsed.details.find((detail) => detail.message.trim())?.message.trim();
  if (fallbackDetail) return fallbackDetail;

  return CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
}

export function isGeneratedClaimNumberString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const upper = trimmed.toUpperCase();
  return upper.startsWith('CL') || upper.startsWith('CN');
}

export function readClaimNumberFromTextValue(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (isGeneratedClaimNumberString(trimmed)) return trimmed;
    return '';
  }
  if (!isRecord(value)) return '';
  if (value.text != null) return readClaimNumberFromTextValue(value.text);
  return '';
}

export function readCreateClaimClaimNumber(res: unknown): string {
  const fromText = readClaimNumberFromTextValue(res);
  if (fromText) return fromText;

  if (res == null || typeof res !== 'object') return '';
  const o = res as Record<string, unknown>;

  const fromTextKey = readClaimNumberFromTextValue(o.text);
  if (fromTextKey) return fromTextKey;

  const top = o.claimNumber;
  if (typeof top === 'string' && top.trim()) return top.trim();
  if (typeof top === 'number' && Number.isFinite(top)) return String(top);

  const data = o.data;
  if (!Array.isArray(data) || data.length === 0) return '';
  for (const row of data) {
    if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
    const rowObj = row as Record<string, unknown>;
    const fromRowText = readClaimNumberFromTextValue(rowObj.text);
    if (fromRowText) return fromRowText;
    const cn = rowObj.claimNumber;
    if (typeof cn === 'string' && cn.trim()) return cn.trim();
    if (typeof cn === 'number' && Number.isFinite(cn)) return String(cn);
  }
  return '';
}

export function parseClaimSubmitMutationError(err: unknown): ClaimSubmitErrorParseResult {
  if (isPcmiServerErrorStatus(readPcmiStatusCode(err))) {
    return {
      summary: PCMI_SERVER_ERROR_MESSAGE,
      details: [],
      claimNumber: readCreateClaimClaimNumber(err),
    };
  }

  const details: ClaimSubmitDetailError[] = [];
  const seen = new Set<string>();
  const root = normalizeClaimSubmitPayload(err);
  const payload = unwrapMutationPayload(root);

  let summary = '';
  if (Array.isArray(payload)) {
    summary = mergeDataArray(payload, summary, details, seen);
  } else if (isRecord(payload)) {
    summary = mergeMessageField(payload.message, summary, details, seen);
    summary = mergeDataArray(payload.data, summary, details, seen);
  }

  scrapeDetailsDeep(root, details, seen);
  if (payload !== root) {
    scrapeDetailsDeep(payload, details, seen);
  }

  if (!summary && err instanceof Error) {
    summary = asUserFacingMessage(err.message);
  }

  const workflowMsg = readWorkflowOutputErrorMessage(err);
  if (workflowMsg) {
    summary = appendWorkflowOutputErrorDetails(workflowMsg, summary, details, seen);
  }

  const claimNumber = readCreateClaimClaimNumber(err);

  return sanitizeClaimSubmitErrorResult({ summary, details, claimNumber });
}
